<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <nav
    v-bind="$attrs"
    class="navbar"
    data-testId="navbar"
    @mouseenter="startHoverCounter"
    @mouseleave="stopHoverCounter"
    @click="handleInteraction"
  >
    <nav-construction-logo v-if="config.flagManager" />
    <nav-logo v-else />

    <navbar-link
      v-if="dashboardRoute && 'id' in dashboardRoute"
      :title="$t(navbarRoutesMap.dashboard.label)"
      :to="{ name: 'dashboard.looker', params: { id: dashboardRoute.id } }"
      data-testid="navbar-dashboards"
      @click="analytics.navbarEvent('Clicked navbar', 'Dashboard')"
    >
      <component :is="navbarRoutesMap.dashboard.icon" />
    </navbar-link>

    <navbar-link
      v-if="!hasWorkspaceRole('AGENT') || selfReviews"
      v-slot="{ active }"
      :title="$t(navbarRoutesMap.conversations.label)"
      data-testid="navbar-conversations"
      :to="{ name: 'conversations' }"
      :class="[disputesCount && $style.notificationBubble]"
      :data-highlight-count="(disputesCount > 99 ? '99+' : disputesCount) || null"
      hide-tooltip
      @click="onConversationsClick"
    >
      <goal v-if="active" />
      <component
        :is="navbarRoutesMap.conversations.icon"
        v-else
        v-tippy="{
          placement: 'right',
          content: $t('modules.application.navbar.conversations'),
          delay,
          offset: [0, 10],
        }"
      />
    </navbar-link>

    <navbar-link
      v-if="!hasWorkspaceRole('AGENT')"
      :title="$t(navbarRoutesMap.tasks.label)"
      data-testid="navbar-tasks"
      :to="{
        name: 'tasks',
        params: assignmentId ? { assignmentId } : { calibrationSessionId },
      }"
      :class="[tasksCount > 0 && $style.notificationBubble]"
      :data-highlight-count="(tasksCount > 99 ? '99+' : tasksCount) || null"
      @click="analytics.navbarEvent('Clicked navbar', 'Tasks')"
    >
      <component :is="navbarRoutesMap.tasks.icon" />
    </navbar-link>

    <navbar-link
      :title="$t(navbarRoutesMap.activity.label)"
      :to="{ name: 'activity' }"
      :class="[activitesCountTotal > 0 && $style.notificationBubble]"
      :data-highlight-count="(activitesCountTotal > 99 ? '99+' : activitesCountTotal) || null"
      data-testid="navbar-activity"
      @click="analytics.navbarEvent('Clicked navbar', 'Activity')"
    >
      <component :is="navbarRoutesMap.activity.icon" />
    </navbar-link>

    <navbar-coaching-icon class="navbar__link" />
  </nav>
</template>

<script lang="ts" setup>
import { computed, ref, defineAsyncComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import config from '@/config'
import { delay } from '@/components/Tippy'
import { session } from '@/composables/useSession'
import { useActivitiesCount } from '@/composables/useActivitiesCount'
import analytics from '@/utils/analytics'
import { hasWorkspaceRole } from '@/utils/roleUtils'
import { bus } from '@/utils/bus'
import useBreakpoints from '@/composables/useBreakpoints'
import { useDashboardsList } from '@/composables/useDashboardsList'
import useNavbarRoutes from '@/composables/useNavbarRoutes'
import useTicketRouteParams from '@/composables/useTicketRouteParams'
import { useTasksIncompleteQuery } from '@/modules/tasks/queries/useTasksIncompleteQuery'

import Goal from './UserGoal.vue'
import NavLogo from './NavLogo/NavLogo.vue'
import NavbarCoachingIcon from './NavbarCoachingIcon.vue'
import NavbarLink from './NavbarLink.vue'

const NavConstructionLogo = defineAsyncComponent(() => import('./NavLogo/NavConstructionLogo.vue'))

const activitiesCount = useActivitiesCount()
const { data } = useTasksIncompleteQuery()
const { dashboardList, lookerReviewsDashboard, lookerReviewsAgentDashboard } = useDashboardsList()
const { assignmentId, calibrationSessionId } = useTicketRouteParams()

const disputesCount = ref(0)

const { navbarRoutesMap } = useNavbarRoutes()
const { breakpoints } = useBreakpoints()
const floatingPageNav = breakpoints.smaller('lg')
// Starts a delay timer to trigger a floating page nav if user does not interact with anything within 400ms
const HOVER_TIMER = 400
let hoverTimeout: ReturnType<typeof setTimeout> | undefined = undefined

const selfReviews = computed(() => session.workspace?.settings?.selfReviews || false)
const activitesCountTotal = computed(() => activitiesCount.value?.total || 0)
const tasksCount = computed(() => (data.value?.agentQa || 0) + (data.value?.reviewerQa || 0))

const route = useRoute()
const router = useRouter()

const dashboardRoute = computed(() => {
  if (dashboardList.value.length && (lookerReviewsDashboard.value || lookerReviewsAgentDashboard.value)) {
    return hasWorkspaceRole('AGENT') ? lookerReviewsAgentDashboard.value : lookerReviewsDashboard.value
  }

  return ''
})

const onConversationsClick = () => {
  analytics.navbarEvent('Clicked navbar', 'Conversations')
  const { singleTicket, ...query } = route.query
  if (singleTicket) router.replace({ query })
}

const startHoverCounter = () => {
  if (!floatingPageNav.value) return
  if (hoverTimeout) clearTimeout(hoverTimeout)
  hoverTimeout = setTimeout(() => {
    hoverTimeout = undefined
    bus.$emit('show-page-nav')
  }, HOVER_TIMER)
}

const stopHoverCounter = () => {
  if (hoverTimeout) {
    clearTimeout(hoverTimeout)
    hoverTimeout = undefined
  }
}

const handleInteraction = (event: MouseEvent) => {
  if (event.target instanceof HTMLElement && event.target.closest('.navbar__link')) {
    stopHoverCounter()
  }
}
</script>

<style lang="scss">
.navbar {
  position: sticky;
  top: 0;
  height: var(--layout-height);
  width: var(--navbar-width);

  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex-shrink: 0;
  z-index: 8;
  background-color: var(--nav-background);

  --sidebar-link-height: 56px;

  &__link {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: var(--sidebar-link-height);
    min-height: var(--sidebar-link-height);
    transition:
      border-color 0.3s ease-out,
      color 0.2s ease-out;
    color: var(--sidebar-link);
    text-align: center;
    outline: none;

    a,
    svg {
      outline: none !important;
    }

    &:hover {
      color: white;

      svg {
        background-color: var(--sidebar-link-background-hover);

        path {
          fill: var(--sidebar-link-fill-hover);
        }
      }
    }

    svg {
      transition: background-color var(--easing-dur-medium) var(--easing-quart);
      height: var(--nav-item-size, var(--sidebar-link-height));
      width: var(--nav-item-size, var(--navbar-width));
      padding: var(--nav-item-padding, 16px);
      border-radius: var(--nav-item-border-radius, 0);

      path {
        fill: var(--sidebar-link-fill);
      }
    }

    svg * {
      stroke-width: var(--sidebar-link-stroke-width);
      vector-effect: var(--sidebar-link-vector-effect);
    }
  }

  & .router-link-active {
    color: white;

    svg {
      background: var(--sidebar-link-background-active);

      path {
        fill: var(--sidebar-link-fill-active);
      }
    }
  }

  &__popup-theme .tippy-content {
    max-width: 275px;
  }
}
</style>

<style module lang="scss">
@use '../styles/navbar.scss' as *;
</style>
